import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import i18n from '@/i18n';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 사용자 시간대 감지 함수
 * @returns {string} 사용자의 시간대 (예: 'Asia/Seoul', 'UTC' 등)
 */
export function getPreferredTimezone() {
  // 1순위: 브라우저 자동 감지 (IE11+)
  try {
    if (window.Intl && Intl.DateTimeFormat) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  } catch (e) {
    console.warn('Intl.DateTimeFormat not supported, using fallback');
  }

  // 2순위: 언어 설정 기반
  const languageTimezones = {
    ko: 'Asia/Seoul',
    en: 'UTC',
    zh: 'Asia/Shanghai',
  };

  return languageTimezones[i18n.language] || 'UTC';
}

/**
 * 날짜 포맷팅 함수
 * @param {string} dateString - 포맷팅할 날짜 문자열
 * @param {string} userTimezone - 사용자 시간대 (선택사항)
 * @returns {string} 포맷팅된 날짜 문자열
 */
export function formatDateWithTimezone(dateString, userTimezone, dateFormat) {
  try {
    if (!dateString) return '';

    // userTimezone이 제공되지 않으면 자동으로 감지
    const timezone = userTimezone || getPreferredTimezone();

    // dayjs를 사용한 시간대 변환 및 포맷팅
    const date = dayjs.utc(dateString).tz(timezone);

    // 언어별 날짜 형식
    const formatMap = {
      ko: 'YYYY년 MM월 DD일',
      en: 'MMM DD, YYYY',
      zh: 'YYYY年MM月DD日',
    };

    const format = dateFormat || formatMap[i18n.language] || 'YYYY-MM-DD';
    return date.format(format);
  } catch (error) {
    // 최종 폴백: 기본 Date 사용
    console.warn('Date formatting failed, using fallback:', error);
    return new Date(dateString).toLocaleDateString();
  }
}

/**
 * 날짜 범위 포맷팅 함수
 * @param {string} startDate - 시작 날짜
 * @param {string} endDate - 종료 날짜
 * @param {string} userTimezone - 사용자 시간대 (선택사항)
 * @returns {string} 포맷팅된 날짜 범위 문자열
 */
export function formatDateRange(startDate, endDate, userTimezone) {
  const formattedStart = formatDateWithTimezone(startDate, userTimezone);
  const formattedEnd = formatDateWithTimezone(endDate, userTimezone);

  if (!formattedStart || !formattedEnd) {
    return formattedStart || formattedEnd || '';
  }

  return `${formattedStart} ~ ${formattedEnd}`;
}

/**
 * 현재 시간대에서 현재 날짜 가져오기
 * @param {string} userTimezone - 사용자 시간대 (선택사항)
 * @returns {string} 포맷팅된 현재 날짜
 */
export function getCurrentDateFormatted(userTimezone) {
  const timezone = userTimezone || getPreferredTimezone();
  const now = dayjs().tz(timezone);

  const formatMap = {
    ko: 'YYYY년 MM월 DD일',
    en: 'MMM DD, YYYY',
    zh: 'YYYY年MM月DD日',
  };

  const format = formatMap[i18n.language] || 'YYYY-MM-DD';
  return now.format(format);
}
